import { useCallback, useRef, type Dispatch, type MutableRefObject, type SetStateAction } from "react"
import { toast } from "sonner"
import { hasCameraPath } from "@/lib/location-scene-3d/camera-path"
import type { Scene3dEngine } from "@/lib/location-scene-3d/engine"
import type { PlatformLayerState } from "@/lib/location-scene-3d/platform-layer"
import { platformIsSet } from "@/lib/location-scene-3d/platform"
import { emptySceneLayout, sceneStateForSave, type SceneCameraState } from "@/lib/location-scene-3d/scene-state"
import {
  createScene3d,
  createScene3dViewpoint,
  updateScene3dScene,
  uploadScene3dPoster,
  type LibraryPrevizItem,
  type LibraryScene3dViewpointItem,
} from "@/lib/library-api"

/** Tile poster on scene switch. Display variant is 800px; previz export stays 1920. */
const LEAVE_POSTER_MAX_EDGE = 800

type TranslateFn = (key: string, options?: object) => string

export type { TranslateFn }

type UseLocationScenePersistArgs = {
  projectId: number
  locationId: number | null
  locationName: string
  sceneMode: "newPreviz" | "editPreviz"
  scene3dId: number | null
  canEdit: boolean
  t: TranslateFn
  onLayoutChanged?: () => void
  onPrevizSaved?: (previz?: LibraryPrevizItem) => void
  /** Viewpoint still saved. Does not mean the editor should close. */
  onViewpointSaved?: (viewpoint?: LibraryScene3dViewpointItem) => void
  engineRef: MutableRefObject<Scene3dEngine | null>
  platformVisualRef: MutableRefObject<PlatformLayerState | null>
  startCameraRef: MutableRefObject<SceneCameraState | null>
  disposedRef: MutableRefObject<boolean>
  dirtyRef: MutableRefObject<boolean>
  existsRef: MutableRefObject<boolean>
  autosaveEnabledRef: MutableRefObject<boolean>
  boundScene3dIdRef: MutableRefObject<number | null>
  setBoundScene3dId: Dispatch<SetStateAction<number | null>>
  setExists: Dispatch<SetStateAction<boolean>>
  setAutosaveEnabled: Dispatch<SetStateAction<boolean>>
}

export function useLocationScenePersist({
  projectId,
  locationId,
  locationName,
  sceneMode,
  scene3dId,
  canEdit,
  t,
  onLayoutChanged,
  onPrevizSaved,
  onViewpointSaved,
  engineRef,
  platformVisualRef,
  startCameraRef,
  disposedRef,
  dirtyRef,
  existsRef,
  autosaveEnabledRef,
  boundScene3dIdRef,
  setBoundScene3dId,
  setExists,
  setAutosaveEnabled,
}: UseLocationScenePersistArgs) {
  const persistTimerRef = useRef<number | null>(null)
  const draftPromiseRef = useRef<Promise<number | null> | null>(null)

  const getSceneStateForPreviz = useCallback(() => {
    const engine = engineRef.current
    const visual = platformVisualRef.current
    if (!engine) return sceneStateForSave(emptySceneLayout())
    return sceneStateForSave({
      ...emptySceneLayout(),
      ...engine.getSceneState(),
      start_camera: startCameraRef.current,
      platform: visual?.platform ?? null,
      cells: visual?.cells ?? [],
      openings: visual?.openings ?? [],
      light: visual?.light ?? null,
      ...(visual ? { faces: visual.faces ?? [] } : {}),
      exists: true,
    })
  }, [engineRef, platformVisualRef, startCameraRef])

  /** Create Scene3d draft once so QR / phone / platform ops see an id. */
  const ensureScene3dDraft = useCallback(
    async (opts?: { force?: boolean }): Promise<number | null> => {
      if (disposedRef.current) return null
      const existing = boundScene3dIdRef.current ?? scene3dId
      if (existing != null) return existing
      if (sceneMode !== "newPreviz" || !canEdit) return null
      if (draftPromiseRef.current) return draftPromiseRef.current

      const engine = engineRef.current
      if (!engine) return null
      const snapshot = engine.getSceneState()
      const visual = platformVisualRef.current
      const meaningful =
        opts?.force ||
        snapshot.objects.length > 0 ||
        hasCameraPath(snapshot.camera_path) ||
        startCameraRef.current != null ||
        platformIsSet(visual?.platform)
      if (!meaningful) return null

      const promise = (async () => {
        try {
          if (disposedRef.current) return null
          const created = await createScene3d(projectId, {
            name: locationName,
            locationId,
            sceneState: getSceneStateForPreviz(),
          })
          if (disposedRef.current) return created.id
          boundScene3dIdRef.current = created.id
          setBoundScene3dId(created.id)
          existsRef.current = true
          setExists(true)
          autosaveEnabledRef.current = true
          setAutosaveEnabled(true)
          return created.id
        } catch (e) {
          if (!disposedRef.current) toast.error(e instanceof Error ? e.message : String(e))
          return null
        } finally {
          draftPromiseRef.current = null
        }
      })()
      draftPromiseRef.current = promise
      return promise
    },
    [
      autosaveEnabledRef,
      boundScene3dIdRef,
      canEdit,
      disposedRef,
      engineRef,
      existsRef,
      getSceneStateForPreviz,
      locationId,
      locationName,
      platformVisualRef,
      scene3dId,
      projectId,
      sceneMode,
      setAutosaveEnabled,
      setBoundScene3dId,
      setExists,
      startCameraRef,
    ],
  )
  const ensureScene3dDraftRef = useRef(ensureScene3dDraft)
  ensureScene3dDraftRef.current = ensureScene3dDraft

  /** @deprecated Alias — callers migrated to ensureScene3dDraft. */
  const ensurePrevizDraft = ensureScene3dDraft
  const ensurePrevizDraftRef = ensureScene3dDraftRef

  const persistNow = useCallback(async (): Promise<boolean> => {
    if (disposedRef.current) return false
    const engine = engineRef.current
    if (!engine || !canEdit) return false
    if (!autosaveEnabledRef.current) return false
    const snapshot = engine.getSceneState()
    const visual = platformVisualRef.current
    const shouldSave =
      existsRef.current ||
      snapshot.objects.length > 0 ||
      hasCameraPath(snapshot.camera_path) ||
      startCameraRef.current != null ||
      platformIsSet(visual?.platform)
    if (!shouldSave || !dirtyRef.current) return false

    const id = await ensureScene3dDraft()
    if (disposedRef.current) return false
    if (id == null) {
      if (sceneMode === "editPreviz") {
        dirtyRef.current = true
      }
      return false
    }
    dirtyRef.current = false
    const payload = sceneStateForSave({
      ...emptySceneLayout(),
      ...snapshot,
      start_camera: startCameraRef.current,
      platform: visual?.platform ?? null,
      cells: visual?.cells ?? [],
      openings: visual?.openings ?? [],
      light: visual?.light ?? null,
      faces: visual?.faces ?? [],
      exists: true,
    })
    try {
      await updateScene3dScene(projectId, id, payload)
      if (disposedRef.current) return false
      existsRef.current = true
      setExists(true)
      onLayoutChanged?.()
      return true
    } catch (e) {
      if (disposedRef.current) return false
      dirtyRef.current = true
      toast.error(e instanceof Error ? e.message : String(e))
      return false
    }
  }, [
    autosaveEnabledRef,
    canEdit,
    dirtyRef,
    disposedRef,
    engineRef,
    ensureScene3dDraft,
    existsRef,
    onLayoutChanged,
    platformVisualRef,
    projectId,
    sceneMode,
    setExists,
    startCameraRef,
  ])

  const queuePersist = useCallback(() => {
    if (disposedRef.current || !canEdit) return
    dirtyRef.current = true
    const snap = engineRef.current?.getSceneState()
    const visual = platformVisualRef.current
    const has = Boolean(
      snap &&
      (snap.objects.length > 0 ||
        hasCameraPath(snap.camera_path) ||
        startCameraRef.current != null ||
        platformIsSet(visual?.platform)),
    )
    existsRef.current = has
    setExists(has)
    if (!autosaveEnabledRef.current) return
    if (persistTimerRef.current != null) window.clearTimeout(persistTimerRef.current)
    persistTimerRef.current = window.setTimeout(() => {
      persistTimerRef.current = null
      void persistNow()
    }, 1500)
  }, [
    autosaveEnabledRef,
    canEdit,
    dirtyRef,
    disposedRef,
    engineRef,
    existsRef,
    persistNow,
    platformVisualRef,
    setExists,
    startCameraRef,
  ])

  const persistObjectsNow = useCallback(() => {
    if (disposedRef.current || !canEdit) return
    dirtyRef.current = true
    if (!autosaveEnabledRef.current) return
    if (persistTimerRef.current != null) {
      window.clearTimeout(persistTimerRef.current)
      persistTimerRef.current = null
    }
    void persistNow()
  }, [autosaveEnabledRef, canEdit, dirtyRef, disposedRef, persistNow])

  /** Still of the live editor, pinned to one scene id. Null when there is nothing to upload. */
  const takePosterSnapshot = useCallback(async (): Promise<{ id: number; blob: Blob } | null> => {
    if (disposedRef.current || !canEdit) return null
    if (persistTimerRef.current != null) {
      window.clearTimeout(persistTimerRef.current)
      persistTimerRef.current = null
    }
    autosaveEnabledRef.current = true
    setAutosaveEnabled(true)
    // Уход пишет всю сцену, даже если двигали только камеру и dirty ещё не стоял.
    dirtyRef.current = true
    await persistNow()
    if (disposedRef.current) return null
    let id = boundScene3dIdRef.current ?? scene3dId
    if (id == null) {
      id = await ensureScene3dDraft({ force: true })
      if (disposedRef.current) return null
    }
    const engine = engineRef.current
    if (id == null || !engine) return null
    try {
      const blob = await engine.captureStillBlob({ includeLightMarker: true })
      if (!blob || disposedRef.current) return null
      return { id, blob }
    } catch (e) {
      if (!disposedRef.current) toast.error(e instanceof Error ? e.message : String(e))
      return null
    }
  }, [
    autosaveEnabledRef,
    boundScene3dIdRef,
    canEdit,
    dirtyRef,
    disposedRef,
    engineRef,
    ensureScene3dDraft,
    persistNow,
    scene3dId,
    setAutosaveEnabled,
  ])

  /** Replace the scene tile poster on close. Does not create a viewpoint. */
  const uploadPosterIfNeeded = useCallback(async (): Promise<boolean> => {
    const snap = await takePosterSnapshot()
    if (!snap) return false
    try {
      await uploadScene3dPoster(projectId, snap.id, snap.blob)
      if (disposedRef.current) return true
      onPrevizSaved?.()
      return true
    } catch (e) {
      if (!disposedRef.current) toast.error(e instanceof Error ? e.message : String(e))
      return false
    }
  }, [disposedRef, onPrevizSaved, projectId, takePosterSnapshot])

  /**
   * Thumbnail switch: snapshot the scene and a small poster, then return.
   * The PUT and JPEG upload keep going after the editor unmounts.
   */
  const schedulePosterUpload = useCallback((): Promise<boolean> => {
    if (!canEdit) return Promise.resolve(false)
    const engine = engineRef.current
    if (!engine) return Promise.resolve(false)
    if (persistTimerRef.current != null) {
      window.clearTimeout(persistTimerRef.current)
      persistTimerRef.current = null
    }
    autosaveEnabledRef.current = true
    if (!disposedRef.current) setAutosaveEnabled(true)
    // Уход пишет всю сцену, даже если двигали только камеру и dirty ещё не стоял.
    dirtyRef.current = true

    const snapshot = engine.getSceneState()
    const visual = platformVisualRef.current
    const shouldSave =
      existsRef.current ||
      snapshot.objects.length > 0 ||
      hasCameraPath(snapshot.camera_path) ||
      startCameraRef.current != null ||
      platformIsSet(visual?.platform)
    const payload = sceneStateForSave({
      ...emptySceneLayout(),
      ...snapshot,
      start_camera: startCameraRef.current,
      platform: visual?.platform ?? null,
      cells: visual?.cells ?? [],
      openings: visual?.openings ?? [],
      light: visual?.light ?? null,
      faces: visual?.faces ?? [],
      exists: true,
    })
    const knownId = boundScene3dIdRef.current ?? scene3dId
    const notify = onPrevizSaved
    const blobTask = engine.captureStillBlob({ includeLightMarker: true, maxEdge: LEAVE_POSTER_MAX_EDGE })

    const publish = (sceneId: number) => {
      if (shouldSave) {
        dirtyRef.current = false
        void updateScene3dScene(projectId, sceneId, payload)
          .then(() => {
            if (disposedRef.current) return
            existsRef.current = true
            setExists(true)
            onLayoutChanged?.()
          })
          .catch((e: unknown) => {
            dirtyRef.current = true
            toast.error(e instanceof Error ? e.message : String(e))
          })
      }
      void blobTask
        .then((blob) => {
          if (!blob) return
          return uploadScene3dPoster(projectId, sceneId, blob).then(() => {
            notify?.()
          })
        })
        .catch((e: unknown) => {
          toast.error(e instanceof Error ? e.message : String(e))
        })
    }

    if (knownId != null) {
      publish(knownId)
    } else {
      void ensureScene3dDraft({ force: true }).then((created) => {
        if (created == null) return
        publish(created)
      })
    }
    return Promise.resolve(true)
  }, [
    autosaveEnabledRef,
    boundScene3dIdRef,
    canEdit,
    dirtyRef,
    disposedRef,
    engineRef,
    ensureScene3dDraft,
    existsRef,
    onLayoutChanged,
    onPrevizSaved,
    platformVisualRef,
    projectId,
    scene3dId,
    setAutosaveEnabled,
    setExists,
    startCameraRef,
  ])

  /** Photo toolbar — Scene3dViewpoint with camera_state (not LocationViewpoint / Previz). */
  const saveViewpointStill = useCallback(async (): Promise<boolean> => {
    if (disposedRef.current || !canEdit) return false
    const engine = engineRef.current
    if (!engine) return false
    try {
      const id = await ensureScene3dDraft({ force: true })
      if (id == null || disposedRef.current) return false
      if (dirtyRef.current) {
        await persistNow()
        if (disposedRef.current) return false
      }
      const blob = await engine.captureStillBlob()
      if (!blob || disposedRef.current) return false
      const cam = engine.getSceneState().camera_state
      const viewpoint = await createScene3dViewpoint(projectId, id, {
        file: blob,
        name: t("locationsPage.scene3d.viewpointName"),
        cameraState: cam ?? {},
      })
      if (disposedRef.current) return true
      toast.success(t("locationsPage.scene3d.viewpointSaved"))
      onViewpointSaved?.(viewpoint)
      return true
    } catch (e) {
      if (!disposedRef.current) toast.error(e instanceof Error ? e.message : String(e))
      return false
    }
  }, [canEdit, dirtyRef, disposedRef, engineRef, ensureScene3dDraft, onViewpointSaved, persistNow, projectId, t])

  /** Phone-fly: persist camera path only — video export is timeline Save. */
  const syncPathAfterPhoneRecord = useCallback(async (): Promise<boolean> => {
    const engine = engineRef.current
    if (!engine || !canEdit) return false
    await engine.whenPathFitSettled()
    if (disposedRef.current) return false
    if (!hasCameraPath(engine.getCameraPath())) return false
    if (!autosaveEnabledRef.current) {
      dirtyRef.current = true
      return true
    }
    if (persistTimerRef.current != null) {
      window.clearTimeout(persistTimerRef.current)
      persistTimerRef.current = null
    }
    dirtyRef.current = true
    return persistNow()
  }, [autosaveEnabledRef, canEdit, dirtyRef, disposedRef, engineRef, persistNow])

  const clearPersistTimer = useCallback(() => {
    if (persistTimerRef.current != null) {
      window.clearTimeout(persistTimerRef.current)
      persistTimerRef.current = null
    }
  }, [])

  return {
    persistTimerRef,
    ensureScene3dDraft,
    ensureScene3dDraftRef,
    ensurePrevizDraft,
    ensurePrevizDraftRef,
    persistNow,
    queuePersist,
    persistObjectsNow,
    uploadPosterIfNeeded,
    schedulePosterUpload,
    saveViewpointStill,
    syncPathAfterPhoneRecord,
    clearPersistTimer,
    getSceneStateForPreviz,
  }
}
